<template>
  <!-- <the-header title="Maison Sèvres"></the-header> -->
  <div id="project-detail" class="main-container">
    <div v-if="isLoading">
      <loading-spinner mode="contained"></loading-spinner>
      <skeleton-list></skeleton-list>
    </div>
    <div v-else>
      <project-cover-image
        :imgSrc="project.coverUrl"
        :projectName="project.name"
      ></project-cover-image>
      <div v-if="!mobile">
        <div style="position:relative;">
          <transition name="action">
            <i
              v-if="editIcon && (activeTab === 1 || activeTab === 2)"
              @click="editTab"
              class="edit-icon pi pi-pencil"
            ></i>
          </transition>
          <div v-if="addSpinner">
            <loading-spinner
              mode="contained"
              class="add-spinner"
            ></loading-spinner>
          </div>
          <TabView
            v-model:activeIndex="activeTab"
            @tab-change="tabChanged"
            @tab-click="tabClicked"
          >
            <TabPanel>
              <template #header>
                <div class="p-d-flex p-jc-between">
                  <div>
                    <i class="pi pi-calendar header-icon"></i>
                    <span>Reminders</span>
                  </div>
                </div>
              </template>
              <project-widgets :projectId="projectId"></project-widgets>
            </TabPanel>
            <TabPanel>
              <template #header>
                <div class="p-d-flex p-jc-between">
                  <div>
                    <i class="pi pi-home header-icon"></i>
                    <span>Project</span>
                  </div>
                </div>
              </template>
              <form-info
                :mobile="mobile"
                :data="project"
                type="project"
                :id="projectId"
                :edit="editing && activeTab === 1"
              ></form-info>
            </TabPanel>
            <TabPanel>
              <template #header>
                <div class="p-d-flex p-jc-between">
                  <div>
                    <i class="pi pi-user header-icon"></i>
                    <span>Client</span>
                  </div>
                </div>
              </template>
              <form-info
                :mobile="mobile"
                :data="client"
                type="client"
                :id="project.clientId"
                :edit="editing && activeTab === 2"
              ></form-info>
            </TabPanel>
            <TabPanel>
              <template #header>
                <div class="p-d-flex p-jc-between">
                  <div>
                    <i class="pi pi-image header-icon"></i>
                    <span>Images</span>
                  </div>
                </div>
              </template>
              <items-grid
                :items="images"
                type="images"
                :multiple="true"
                @item-clicked="imageClicked"
                @selected="addImages"
              ></items-grid>
              <Galleria
                v-if="images.length"
                :value="images"
                :circular="true"
                :fullScreen="true"
                :showItemNavigators="true"
                :showThumbnails="false"
                containerStyle="max-width: 70%"
                v-model:activeIndex="imageIndex"
                v-model:visible="showGallery"
              >
                <template #item="slotProps">
                  <img
                    :src="slotProps.item.url"
                    :alt="slotProps.item.alt"
                    style="width: 100%; display: block;"
                  />
                </template>
              </Galleria>

              <div v-if="showGallery" class="gallery-delete">
                <i
                  v-if="!deleteSpinner"
                  class="pi pi-trash"
                  @click="deleteImage"
                ></i>
                <loading-spinner
                  v-else
                  mode="contained"
                  class="delete-spinner"
                ></loading-spinner>
              </div>
            </TabPanel>
            <TabPanel>
              <template #header>
                <div class="p-d-flex p-jc-between">
                  <div>
                    <i class="pi pi-folder header-icon"></i>
                    <span
                      >Documents
                      <span v-if="folderOpen"
                        >/ {{ selectedFolder.display }}</span
                      ></span
                    >
                  </div>
                </div>
              </template>
              <items-grid
                v-if="!folderOpen"
                :items="folders"
                type="icons"
                :multiple="true"
                :customPlusAction="true"
                @custom-plus-action="createFolder"
                @item-clicked="folderClicked"
                @edit-item="editItem($event, 'folder')"
                @delete-item="deleteItem($event, 'folder')"
              ></items-grid>
              <items-grid
                v-else
                :items="selectedFolder.documents"
                type="icons"
                :multiple="true"
                :uploadOnEntry="creatingFolder"
                @item-clicked="documentClicked"
                @selected="addDocuments"
                @delete-item="deleteItem($event, 'document')"
                @edit-item="editItem($event, 'document')"
              ></items-grid>
            </TabPanel>
          </TabView>
        </div>
      </div>
      <div v-else>
        <Accordion
          :multiple="true"
          @tab-open="tabOpened"
          @tab-close="tabClosed"
        >
          <AccordionTab>
            <template #header>
              <div class="accordion-header p-d-flex p-jc-between">
                <div>
                  <i class="pi pi-home header-icon"></i>
                  <span>Project Information</span>
                </div>
                <transition name="action">
                  <div
                    v-if="openTabs.includes(0)"
                    @click="editTabInfo($event, 0)"
                    class="action-icon"
                  >
                    <i class="pi pi-pencil"></i>
                  </div>
                </transition>
              </div>
            </template>
            <form-info
              :mobile="mobile"
              :data="project"
              type="project"
              :id="projectId"
              :edit="editTabs.includes(0)"
            ></form-info>
          </AccordionTab>
          <AccordionTab>
            <template #header>
              <div class="accordion-header p-d-flex p-jc-between">
                <div>
                  <i class="pi pi-user header-icon"></i>
                  <span>Client Information</span>
                </div>
                <transition name="action">
                  <div
                    v-if="openTabs.includes(1)"
                    @click="editTabInfo($event, 1)"
                    class="action-icon"
                  >
                    <i class="pi pi-pencil"></i>
                  </div>
                </transition>
              </div>
            </template>
            <form-info
              :mobile="mobile"
              :data="client"
              type="client"
              :id="project.clientId"
              :edit="editTabs.includes(1)"
            ></form-info>
          </AccordionTab>
          <AccordionTab>
            <template #header>
              <div class="accordion-header p-d-flex p-jc-between">
                <div>
                  <i class="pi pi-image header-icon"></i>
                  <span>Project Images</span>
                </div>
              </div>

              <div v-if="addSpinner">
                <loading-spinner
                  mode="contained"
                  class="add-spinner"
                ></loading-spinner>
              </div>
            </template>
            <items-grid
              :items="images"
              type="images"
              :multiple="true"
              @item-clicked="imageClicked"
              @selected="addImages"
            ></items-grid>
            <Galleria
              v-if="images.length"
              :value="images"
              :circular="true"
              :fullScreen="true"
              :showItemNavigators="true"
              :showThumbnails="false"
              v-model:activeIndex="imageIndex"
              v-model:visible="showGallery"
            >
              <template #item="slotProps">
                <img
                  :src="slotProps.item.url"
                  :alt="slotProps.item.alt"
                  style="width: 100%; display: block;"
                />
              </template>
            </Galleria>

            <div v-if="showGallery" class="gallery-delete">
              <i
                v-if="!deleteSpinner"
                class="pi pi-trash"
                @click="deleteImage"
              ></i>
              <loading-spinner
                v-else
                mode="contained"
                class="delete-spinner"
              ></loading-spinner>
            </div>
          </AccordionTab>
          <AccordionTab>
            <template #header>
              <div class="accordion-header p-d-flex p-jc-between">
                <div>
                  <i class="pi pi-folder header-icon"></i>
                  <span
                    >Documents
                    <span v-if="folderOpen"
                      >/ {{ selectedFolder.display }}</span
                    ></span
                  >
                </div>
              </div>
              <div v-if="addSpinner">
                <loading-spinner
                  mode="contained"
                  class="add-spinner"
                ></loading-spinner>
              </div>
            </template>
            <items-grid
              v-if="!folderOpen"
              :items="folders"
              type="icons"
              :multiple="true"
              :customPlusAction="true"
              @custom-plus-action="createFolder"
              @item-clicked="folderClicked"
              @edit-item="editItem($event, 'folder')"
              @delete-item="deleteItem($event, 'folder')"
            ></items-grid>
            <items-grid
              v-else
              :items="selectedFolder.documents"
              type="icons"
              :multiple="true"
              :uploadOnEntry="creatingFolder"
              @item-clicked="documentClicked"
              @selected="addDocuments"
              @delete-item="deleteItem($event, 'document')"
              @edit-item="editItem($event, 'document')"
            ></items-grid>
          </AccordionTab>
        </Accordion>
      </div>
    </div>

    <Dialog
      v-model:visible="displayTodoModal"
      :dismissableMask="true"
      :modal="true"
    >
      <todo-list
        @resize="displayTodoModal = false"
        :projectId="projectId"
        expandLabel="Minimize"
      ></todo-list>
    </Dialog>

    <input
      ref="fileInput"
      type="file"
      multiple="true"
      @input="entryItemsSelected"
      hidden
    />
  </div>
</template>

<script>
import { ref } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import useIsMobile from "../composables/is-mobile.js";
import useDocuments from "../composables/documents.js";

import formInfo from "../components/FormInfo.vue";
import SkeletonList from "../components/UI/SkeletonList.vue";
import ItemsGrid from "../components/ItemsGrid.vue";

import ProjectCoverImage from "../components/ProjectCoverImage.vue";
import ProjectWidgets from "../components/ProjectsWidgets.vue";

import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import Galleria from "primevue/galleria";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";

export default {
  components: {
    SkeletonList,
    formInfo,
    ItemsGrid,
    ProjectCoverImage,
    ProjectWidgets,
    Accordion,
    AccordionTab,
    Galleria,
    TabView,
    TabPanel
  },
  setup() {
    const { isMobile } = useIsMobile();
    const mobile = isMobile();
    const isLoading = ref(true);
    const route = useRoute();
    const projectId = route.params.projectId;
    const store = useStore();

    const project = ref(null);
    const client = ref(null);
    const images = ref([]);
    const showGallery = ref(false);

    const displayTodoModal = ref(false);

    async function getProject() {
      project.value = await store.dispatch("getProjectById", projectId);
      if (project.value.clientId) {
        client.value = await store.dispatch(
          "getClientById",
          project.value.clientId
        );
      }
      const imagesListRef = await store.dispatch(
        "listProjectImages",
        projectId
      );
      for (const imgRef of imagesListRef.items) {
        const downloadUrl = await store.dispatch(
          "getImgDownloadUrl",
          imgRef.fullPath
        );
        images.value.push({
          url: downloadUrl,
          path: imgRef.fullPath,
          alt: "Project image"
        });
      }

      await fetchFoldersList(projectId, `${projectId}/documents`);
      isLoading.value = false;
    }

    const {
      fetchFoldersList,
      folders,
      fetchAllDocuments,
      selectedFolder,
      createLocalFolder,
      uploadDocuments,
      deleteDocument,
      changeDocumentName,
      changeFolderName,
      deleteFolder,
      removeLocalFolder,
      itemNameTaken
    } = useDocuments();

    const creatingFolder = ref(false);
    const fileInput = ref(null);

    function createFolder() {
      const localFolder = createLocalFolder(projectId);
      if (localFolder) {
        fileInput.value.click();
        creatingFolder.value = true;
        const newFolder = true;
        openFolder(localFolder.name, newFolder);
      }
    }

    async function entryItemsSelected(ev) {
      await addDocuments(ev.target.files);
    }

    function closeFolder() {
      folderOpen.value = false;
      if (selectedFolder.value && !selectedFolder.value.documents.length) {
        removeLocalFolder(selectedFolder.value.name);
        if (creatingFolder.value === true) {
          alert("Folder not created because no files were uploaded");
        }
      }
    }

    async function editItem(ev, type) {
      let nameVal = prompt("Enter new name");
      if (!nameVal || (nameVal && !nameVal.trim())) return;
      const newName = nameVal.trim();
      if (itemNameTaken(newName, type) === false) {
        addSpinner.value = true;
        if (type === "document") {
          await changeDocumentName(newName, ev.item.fullPath, ev.index);
        } else if (type === "folder") {
          await changeFolderName(ev.item, ev.index, newName);
        }
        addSpinner.value = false;
      }
    }

    async function deleteItem(ev, type) {
      const confirmDelete = confirm(
        `Are you sure you want to delete this ${type} ? This action is irreversible`
      );

      if (confirmDelete) {
        addSpinner.value = true;
        if (type === "folder") {
          await deleteFolder(ev.item, ev.index);
        } else if (type === "document") {
          const folderEmpty = await deleteDocument(ev.item, ev.index);
          if (folderEmpty === true) {
            folderOpen.value = false;
            alert("Folder deleted because it is now empty");
          }
        }
        addSpinner.value = false;
      }
    }

    function folderClicked(ev) {
      const folderName = ev.item.name;
      openFolder(folderName);
    }

    async function openFolder(folderName, newFolder = false) {
      addSpinner.value = true;
      await fetchAllDocuments(folderName, newFolder);
      addSpinner.value = false;
      folderOpen.value = true;
    }

    async function addDocuments(files) {
      addSpinner.value = true;
      await uploadDocuments(files, projectId);
      addSpinner.value = false;
    }

    function documentClicked(ev) {
      window.open(ev.item.url, "_blank");
    }

    getProject();

    const folderOpen = ref(null);

    const imageIndex = ref(0);
    const deleteSpinner = ref(false);

    function imageClicked(ev) {
      imageIndex.value = ev.index;
      showGallery.value = true;
    }

    async function deleteImage() {
      const deleteIndex = imageIndex.value;
      const toDelete = images.value[deleteIndex];
      deleteSpinner.value = true;

      if (images.value.length === deleteIndex + 1) {
        if (images.value.length > 1) {
          imageIndex.value = deleteIndex - 1;
        }
      }

      await store.dispatch("deleteStorageItem", toDelete.path);

      images.value.splice(deleteIndex, 1);

      if (!images.value.length) {
        showGallery.value = false;
      }
      deleteSpinner.value = false;
    }

    const addSpinner = ref(false);
    async function addImages(files) {
      addSpinner.value = true;
      for (const file of files) {
        const res = await store.dispatch("uploadImage", {
          path: projectId,
          image: file
        });

        const downloadUrl = await store.dispatch(
          "getImgDownloadUrl",
          res.ref.fullPath
        );

        images.value.push({
          url: downloadUrl,
          path: res.ref.fullPath,
          alt: "Project image"
        });
      }
      addSpinner.value = false;
    }

    const activeTab = ref(0);

    const openTabs = ref([]);
    function tabOpened(ev) {
      openTabs.value.push(ev.index);
    }

    function tabClosed(ev) {
      openTabs.value = openTabs.value.filter(tabIndex => tabIndex !== ev.index);
      if (editTabs.value.includes(ev.index)) {
        editTabInfo(null, ev.index);
      }
      if (ev.index === 3) {
        folderOpen.value = false;
      }
    }

    const editIcon = ref(true);
    function tabChanged(ev) {
      if (ev.index !== 3) {
        closeFolder();
      }
      editIcon.value = false;
      editing.value = false;
      setTimeout(() => {
        editIcon.value = true;
      }, 500);
    }

    function tabClicked() {
      if (activeTab.value === 4 && selectedFolder.value) {
        closeFolder();
      }
      creatingFolder.value = false;
    }

    const editing = ref(false);
    function editTab() {
      editing.value = !editing.value;
    }

    const editTabs = ref([]);
    function editTabInfo(ev, tabIndex) {
      if (ev) ev.stopPropagation();
      const res = editTabs.value.findIndex(x => x === tabIndex);
      if (res === -1) editTabs.value.push(tabIndex);
      else editTabs.value.splice(res, 1);
    }

    return {
      displayTodoModal,
      mobile,
      isLoading,
      project,
      images,
      showGallery,
      imageClicked,
      imageIndex,
      deleteImage,
      addImages,
      projectId,
      client,
      openTabs,
      tabOpened,
      tabChanged,
      tabClosed,
      editing,
      editTab,
      editIcon,
      editTabInfo,
      editTabs,
      activeTab,
      deleteSpinner,
      addSpinner,
      folderClicked,
      documentClicked,
      folderOpen,
      folders,
      selectedFolder,
      tabClicked,
      createFolder,
      creatingFolder,
      addDocuments,
      deleteItem,
      editItem,
      fileInput,
      entryItemsSelected
    };
  }
};
</script>

<style lang="scss" scoped>
.todo-list-modal {
  color: red !important;
}
i {
  font-size: 1.5rem;
}

.p-dialog .p-dialog-header {
  padding: 0 !important;
}

.header-icon {
  margin-right: 1rem;
}

.edit-icon {
  position: absolute;
  right: 1rem;
  top: 1.1rem;
  z-index: 10;
  color: #fff;
  cursor: pointer;
}

.spinner-container.add-spinner {
  position: absolute;
  right: 1rem;
  top: 0.5rem;
  z-index: 10;
}

span {
  font-size: 1.3rem;
}

.accordion-enter-active {
  animation: opacify linear $animation-fast;
}

.action-enter-active {
  animation: opacify linear $animation-fast,
    scale-down-up linear $animation-slow;
}

.action-leave-active {
  animation: opacify linear $animation-fast reverse;
}

.gallery-delete {
  position: fixed;
  top: 0px;
  left: 0;
  margin: 0.5rem;
  width: 4rem;
  height: 4rem;
  color: #f8f9fa;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  border-radius: 50%;
  z-index: 1500;
  transition: background-color 0.1s;
  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }

  i {
    font-size: 2rem;
    cursor: pointer;
  }
}

@keyframes opacify {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes spin2 {
  100% {
    transform: rotate(-360deg);
  }
}

@keyframes scale-down-up {
  0% {
    transform: scale(0.7);
  }
  50% {
    transform: scale(1.5);
  }
  100% {
    transform: scale(1);
  }
}

.title {
  text-align: center;
}

.action-icon {
  padding-left: 30px;
  z-index: 10;
  pointer-events: all;
}

.spinner-container.delete-spinner {
  position: initial;
}

@media only screen and (max-width: $screen-small) {
  .spinner-container {
    top: calc(1rem + #{$mobile-bar});
  }

  .spinner-container.delete-spinner {
    left: 1rem;
    right: auto;
  }

  .spinner-container.add-spinner {
    top: 0.5rem;
    right: 0.5rem;
  }

  .title {
    text-align: left;
  }

  .action-icon {
    width: auto;
    text-align: auto;
  }

  .custom-skeleton {
    padding-left: 0 !important;
  }

  .p-skeleton {
    height: 50vh;
  }

  .accordion-header {
    width: 100%;
  }

  .header-icon {
    margin-left: 0.5rem;
  }

  .background-red {
    background-color: red;
  }

  .background-blue {
    background-color: blue;
  }

  .p-galleria-mask .p-galleria-item-nav {
    position: absolute !important;
  }
}
</style>
